import {
  formatEcho,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/physics-fields/format";
import type {
  Arrangement,
  HookeSolution,
  HookeTarget,
  OscillatorSolution,
  OscillatorTarget,
} from "@/lib/tool-kit/physics-fields/spring";

/** Equation, substitution and answer, in SI. Symbols are not translated. */

export function hookeWorking(
  target: HookeTarget,
  s: HookeSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const lines: string[] = [];
  if (target === "stiffness") {
    lines.push(
      "k = F / x",
      `k = ${e(s.force)} / ${e(s.extension)} = ${r(s.stiffness, "N/m")}`
    );
  } else if (target === "extension") {
    lines.push(
      "x = F / k",
      `x = ${e(s.force)} / ${e(s.stiffness)} = ${r(s.extension, "m")}`
    );
  } else {
    lines.push(
      "F = k x",
      `F = ${e(s.stiffness)} × ${e(s.extension)} = ${r(s.force, "N")}`
    );
  }
  lines.push(
    "U = ½ k x²",
    `U = ½ × ${e(s.stiffness)} × (${e(s.extension)})² = ${r(s.energy, "J")}`
  );
  return lines;
}

export function comboWorking(
  stiffnesses: readonly number[],
  arrangement: Arrangement,
  equivalent: number,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const result = `${formatSig(fmt, equivalent, sig)} N/m`;
  if (arrangement === "series") {
    const terms = stiffnesses.map((k) => `1/${e(k)}`).join(" + ");
    return [
      "1/kₛ = 1/k₁ + 1/k₂ + …",
      `1/kₛ = ${terms} = ${e(1 / equivalent)} m/N`,
      `kₛ = ${result}`,
    ];
  }
  return [
    "kₚ = k₁ + k₂ + …",
    `kₚ = ${stiffnesses.map(e).join(" + ")} = ${result}`,
  ];
}

export function oscillatorWorking(
  target: OscillatorTarget,
  s: OscillatorSolution,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const e = (v: number) => formatEcho(fmt, v);
  const r = (v: number, unit: string) => `${formatSig(fmt, v, sig)} ${unit}`;
  const lines: string[] = [];
  if (target === "mass") {
    lines.push(
      "m = k T² / (4π²)",
      `m = ${e(s.stiffness)} × (${e(s.period)})² / (4π²) = ${r(s.mass, "kg")}`
    );
  } else if (target === "stiffness") {
    lines.push(
      "k = 4π² m / T²",
      `k = 4π² × ${e(s.mass)} / (${e(s.period)})² = ${r(s.stiffness, "N/m")}`
    );
  } else {
    lines.push(
      "T = 2π √(m / k)",
      `T = 2π × √(${e(s.mass)} / ${e(s.stiffness)}) = ${r(s.period, "s")}`
    );
  }
  lines.push(`ω = 2π / T = √(k / m) = ${r(s.angularFrequency, "rad/s")}`);
  if (s.amplitude !== null && s.maxSpeed !== null) {
    lines.push(
      `v_max = A ω = ${e(s.amplitude)} × ${e(s.angularFrequency)} = ${r(s.maxSpeed, "m/s")}`,
      `a_max = A ω² = ${r(s.maxAcceleration ?? 0, "m/s²")}`,
      `E = ½ k A² = ${r(s.energy ?? 0, "J")}`
    );
  }
  return lines;
}
